"use client"
import { sendGTMEvent } from "@next/third-parties/google"

export const pageview = () => {
  sendGTMEvent({ event: "pageview" })
}

/**
 * Send a GTM event for menu navigation clicks.
 * @param menuType - The menu type (e.g., "תפריט סטיקי")
 * @param menuCategory1 - The first category value (e.g., "ביטוח הרכב שלנו")
 * @param menuCategory2 - The second category value (e.g., click text)
 * @param menuCategory3 - The third category value
 */
export const menuNavigationClick = ({
  menuType,
  menuCategory1,
  menuCategory2,
  menuCategory3,
}: {
  menuType: string
  menuCategory1: string
  menuCategory2?: string | null
  menuCategory3?: string | null
}) => {
  // Helper to convert to snake_case (preserve Hebrew, English, digits, and underscores)
  const toSnake = (val: string) =>
    val
      ? val
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^A-Za-z0-9_\u0590-\u05FF]/g, "_")
          .replace(/_+$/g, "")
      : undefined

  sendGTMEvent({
    event: "menu_navigation_click",
    menu_type: toSnake(menuType),
    menu_category1: menuCategory1 ? toSnake(menuCategory1) : null,
    menu_category2: menuCategory2 ? toSnake(menuCategory2) : null,
    menu_category3: menuCategory3 ? toSnake(menuCategory3) : null,
  })
}

/**
 * Send a generic GTM event for block interactions.
 * @param params - Event params
 * @param params.pathname - The current page path (e.g. "/actions/car-insurance")
 * @param params.id - The block id (should contain a number, e.g. "block-8")
 * @param params.title - The block title (string, will be converted to snake_case)
 * @param params.componentName - The component name (string, will be converted to snake_case)
 * @param params.productName - The product name (string, will be converted to snake_case)
 * @param params.actionName - The action name (string, will be converted to snake_case)
 * @param params.buttonName - The button name (string, will be converted to snake_case)
 * @param params.EventPrefix - The event prefix (string, default is "click")
 * @param params.serviceName - The service name (string, will be converted to snake_case)
 */
export const sendBlockEvent = ({
  blockId,
  title,
  componentName,
  productName,
  actionName,
  buttonName,
  eventPrefix = "click",
  serviceName,
}: {
  blockId: string
  title?: string
  componentName: string
  productName?: string
  actionName?: string
  buttonName?: string
  eventPrefix?: string
  serviceName?: string
}) => {
  // Get pathname from window.location
  let pathname = typeof window !== "undefined" ? window.location.pathname : ""
  if (pathname === "/") pathname = "hp"
  // Remove leading/trailing slashes, replace / with _, and prepend "click_"
  const event =
    `${eventPrefix}_` +
    pathname
      .replace(/^\/+|\/+$/g, "")
      .replace(/\//g, "_")
      .replace(/\s+/g, "_")
      .toLowerCase()

  // Extract the first number from the blockId string
  const component_no = blockId?.match(/\d+/)
    ? parseInt(blockId.match(/\d+/)?.[0] || "0") + 1
    : 0

  // Helper to convert to snake_case (preserve Hebrew, English, digits, and underscores)
  const toSnake = (val: string) =>
    val
      ? val
          .trim()
          .replace(/\s+/g, "_")
          .replace(/[^A-Za-z0-9_\u0590-\u05FF]/g, "_") // allow English, digits, _, and Hebrew
          .replace(/_+$/g, "") // remove trailing underscores
      : ""

  sendGTMEvent({
    event,
    title: title ? toSnake(title) : undefined,
    component_name: toSnake(componentName),
    component_no,
    product_name: productName ? toSnake(productName) : undefined,
    action_name: actionName ? toSnake(actionName) : undefined,
    button_name: buttonName ? toSnake(buttonName) : undefined,
    service_name: serviceName ? toSnake(serviceName) : undefined,
  })
}
export const submitClick = (text: string, name: string) => {
  sendGTMEvent({
    event: "join form click",
    click_text: text, // The text displayed on the button that was clicked.
    element_name: name, // The name of the form
  })
}

export const submitError = (
  text: string,
  name: string,
  error: string | undefined,
) => {
  sendGTMEvent({
    event: "error join form",
    click_text: text, // The text displayed on the button that was clicked.
    element_name: name, // The name of the UI element that was interacted with.
    error_name: error, // Error name – not all details filled, terms of use not approved.
  })
}

export const submitSuccess = (text: string, name: string, approve: boolean) => {
  sendGTMEvent({
    event: "join form success",
    click_text: text, // The text displayed on the button that was clicked.
    element_name: name, // The name of the UI element that was interacted with.
    terms_of_use: approve, // TRUE / FALSE indication based on the user's choice.
  })
}
